import React, { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Navigate, ScrollRestoration } from 'react-router-dom'
import './global.css'
import './../node_modules/animate.css/animate.min.css'

import { AuthProvider } from './contexts/AuthContext'

import RootLayout, { loader as rootLayoutLoader } from './routes/rootLayout'
import ErrorPage from './error'
import Home, { loader as homeLoader } from './routes/home'
import VirtualTour, { loader as virtualTourLoader } from './routes/virtual-tour'
import TourDetails, { loader as tourDetailsLoader } from './routes/tour-details'
import Event, { loader as eventLoader } from './routes/event'
import Gis, { loader as gisLoader } from './routes/gis'
import CitizenshipEducation, { loader as citizenshipEducationLoader } from './routes/citizenship-education'
import Link, { loader as linkLoader } from './routes/link'
import TelephoneNumber, { loader as telephoneNumberLoader } from './routes/telephoneNumber'
import Pantheon, { loader as pantheonLoader } from './routes/pantheon'
import Project, { loader as projectLoader } from './routes/project'
import Book, { loader as bookLoader } from './routes/book'

import Idea from './routes/idea'
import IdeaNew, { loader as ideaNewLoader } from './routes/ideaNew'
import IdeaEdit, { loader as ideaEditLoader, action as ideaEditAction } from './routes/ideaEdit'
import IdeaList from './routes/ideaList'

import Complaint from './routes/complaint'
import ComplaintNew, { loader as complaintNewLoader } from './routes/complaintNew'
import ComplaintEdit, { loader as complaintEditLoader, action as complaintEditAction } from './routes/complaintEdit'
import ComplaintList from './routes/complaintList'

import Competition from './routes/competition'
import CompetitionNew, { loader as competitionNewLoader } from './routes/competitionNew'
import CompetitionEdit, { loader as competitionEditLoader, action as competitionEditAction } from './routes/competitionEdit'
import CompetitionList from './routes/competitionList'


import Passage from './routes/passage'
import PassageNew, { loader as passageNewLoader, action as passageNewAction } from './routes/passageNew'
import PassageEdit, { loader as passageEditLoader, action as passageEditAction } from './routes/passageEdit'
import PassageList from './routes/passageList'

import PassagePrint from './routes/passagePrint'

import CourseSport, { loader as courseSportLoader } from './routes/courseSport'
import CourseEducation, { loader as courseEducationLoader } from './routes/courseEducation'
import CourseCulture, { loader as courseCultureLoader } from './routes/courseCulture'

// Course
const Course = lazy(() => import('./routes/course'))

const Platform137 = lazy(() => import('./routes/platform137'))
const Muharram = lazy(() => import('./routes/muharram'))
const MuharramPrayer = lazy(() => import('./routes/muharram-prayer'))
const MuharramPrayerContent = lazy(() => import('./routes/muharram-prayer-content'))
const PrayerVideo = lazy(() => import('./routes/prayer-video'))
const MuharramDasteh = lazy(() => import('./routes/muharram-dasteh'))
const MuharramPrayerDays = lazy(() => import('./routes/muharram-prayer-days'))
const MuharramHadith = lazy(() => import('./routes/muharram-hadith'))
const MuharramHowToPray = lazy(() => import('./routes/muharram-how-to-pray'))
const MuharramAshora = lazy(() => import('./routes/muharram-ashora'))
const MuharramRegulation = lazy(() => import('./routes/muharram-regulation'))
const MuharramTeshtgozari = lazy(() => import('./routes/muharram-teshtgozari'))
const MuharramVoice = lazy(() => import('./routes/muharram-voice'))
const MuharramArbaeinHosseini = lazy(() => import('./routes/muharram-arbaein-hosseini'))
const MuharramAshoraHosseini = lazy(() => import('./routes/muharram-ashora-hosseini'))
const MuharramTasoaHosseini = lazy(() => import('./routes/muharram-tasoa-hosseini'))
const MuharramWelcome = lazy(() => import('./routes/muharram-welcome'))
const MuharramCalendar = lazy(() => import('./routes/muharram-calendar'))
const MuharramChilderen = lazy(() => import('./routes/muharram-children'))
const MuharramCandleBroadcast = lazy(() => import('./routes/muharram-candle-broadcast'))
const Quran = lazy(() => import('./routes/quran'))

const CityBuilding = lazy(() => import('./routes/city-building'))
const Contact = lazy(() => import('./routes/contact'))
const Setting = lazy(() => import('./routes/setting'))
const About = lazy(() => import('./routes/about'))
const Guide = lazy(() => import('./routes/guide'))
const BrandResource = lazy(() => import('./routes/brand-resource'))
const HowToUse = lazy(() => import('./routes/how-to-use'))
const Privacy = lazy(() => import('./routes/privacy'))
const Download = lazy(() => import('./routes/download'))
const Shohada = lazy(() => import('./routes/shohada'))
const Billboard = lazy(() => import('./routes/billboard'))
const Tree = lazy(() => import('./routes/tree'))
const PrayerTimes = lazy(() => import('./routes/prayer-times'))
const Weather = lazy(() => import('./routes/weather'))
const Map = lazy(() => import('./routes/map'))
const Area = lazy(() => import('./routes/area'))
const Think = lazy(() => import('./routes/think'))
const Ardabil = lazy(() => import('./routes/ardabil'))
const Correspondence = lazy(() => import('./routes/correspondence'))
const Pages = lazy(() => import('./routes/pages'))
const Tourism = lazy(() => import('./routes/tourism'))
const ArdabilTourism = lazy(() => import('./routes/ardabil-tourism'))
const ArdabilTourismDetail = lazy(() => import('./routes/ardabil-tourism-detail'))
const ArdabilTourismCityDetail = lazy(() => import('./routes/ardabil-tourism-city-detail'))
const Platform133 = lazy(() => import('./routes/platform133'))
const TourismComments = lazy(() => import('./routes/tourism-comments'))
const Discount = lazy(() => import('./routes/discount'))
const Transportation = lazy(() => import('./routes/transportation'))
const City = lazy(() => import('./routes/city'))
const Software = lazy(() => import('./routes/software'))
const Vehicle = lazy(() => import('./routes/vehicle'))
const Parking = lazy(() => import('./routes/parking'))
const Auction = lazy(() => import('./routes/auction'))
const Login = lazy(() => import('./routes/login'))
const Profile = lazy(() => import('./routes/profile'))
const Esferis = lazy(() => import('./routes/esferis'))
const Basij = lazy(() => import('./routes/basij'))
const Clor = lazy(() => import('./routes/clor'))
const Martyr = lazy(() => import('./routes/martyr'))
const Shorabil = lazy(() => import('./routes/shorabil'))
const Handicrafts = lazy(() => import('./routes/handicrafts'))
const Anthropology = lazy(() => import('./routes/anthropology'))
const Gorgor = lazy(() => import('./routes/gorgor'))
const Demoli = lazy(() => import('./routes/demoli'))
const Barandagh = lazy(() => import('./routes/barandagh'))
const Kanjagh = lazy(() => import('./routes/kanjagh'))
const Onar = lazy(() => import('./routes/onar'))
const Moallagh = lazy(() => import('./routes/moallagh'))
const Bana = lazy(() => import('./routes/bana'))
const Castle = lazy(() => import('./routes/castle'))
const Shiekhsafi = lazy(() => import('./routes/shiekhsafi'))
const Shiekhheydar = lazy(() => import('./routes/shiekhheydar'))
const Shahryeri = lazy(() => import('./routes/shahryeri'))
const Archaeology = lazy(() => import('./routes/archaeology'))
const Emamschool = lazy(() => import('./routes/emamschool'))
const Sardabeh = lazy(() => import('./routes/sardabeh'))
const HazelnutForest = lazy(() => import('./routes/hazelnut-forest'))
const Erdmosa = lazy(() => import('./routes/erdmosa'))
const NationalAnthem = lazy(() => import('./routes/national-anthem'))
const Mosque = lazy(() => import('./routes/mosque'))

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <AuthProvider>
          <RootLayout />
        </AuthProvider>
        <ScrollRestoration />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        loader: homeLoader,
        element: <Home />,
      },
      {
        path: 'login',
        element: <Login />,
      },

      {
        path: 'profile',
        element: <Profile />,
      },
      {
        path: 'legal',
        children: [
          {
            path: '',
            element: <Navigate to="/legal/privacy-policy" replace />,
          },
          {
            path: 'how-to-use',
            element: <HowToUse />,
          },
          {
            path: 'privacy-policy',
            element: <Privacy />,
          },
        ],
      },
      {
        path: 'vr',
        children: [
          {
            path: '',
            loader: virtualTourLoader,
            element: <VirtualTour />,
          },
          {
            path: ':vrId',
            loader: tourDetailsLoader,
            element: <TourDetails />,
          },
        ],
      },
      {
        path: 'pages/:id',
        element: <Pages />,
      },
      {
        path: 'event',
        children: [
          {
            path: '',
            loader: virtualTourLoader,
            element: <>این صفحه وجود ندارد</>,
          },
          {
            path: ':id',
            loader: eventLoader,
            element: <Event />,
          },
        ],
      },
      {
        path: 'pantheon',
        children: [
          {
            path: '',
            loader: pantheonLoader,
            element: <Pantheon />,
          },
        ],
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'national-anthem',
        element: <NationalAnthem title={`سرود ملی`} />,
      },
      {
        path: '137',
        children: [
          {
            path: '',
            element: <Platform137 />,
          },
          {
            path: ':requestType',
            element: <Platform137 />,
          },
        ],
      },
      {
        path: 'gis',
        element: <Gis />,
        loader: gisLoader,
      },
      {
        path: 'project',
        element: <Project />,
        loader: projectLoader,
      },
      {
        path: 'esferis',
        element: <Esferis />,
      },
      {
        path: 'hazelnut-forest',
        element: <HazelnutForest />,
      },
      {
        path: 'shorabil',
        element: <Shorabil />,
      },
      {
        path: 'shiekhheydar',
        element: <Shiekhheydar />,
      },
      {
        path: 'archaeology',
        element: <Archaeology />,
      },

      {
        path: 'shahryeri',
        element: <Shahryeri />,
      },
      {
        path: 'moallagh',
        element: <Moallagh />,
      },
      {
        path: 'erdmosa',
        element: <Erdmosa />,
      },
      {
        path: 'sardabeh',
        element: <Sardabeh />,
      },
      {
        path: 'emamschool',
        element: <Emamschool />,
      },
      {
        path: 'bana',
        element: <Bana />,
      },
      {
        path: 'shiekhsafi',
        element: <Shiekhsafi />,
      },
      {
        path: 'castle',
        element: <Castle />,
      },
      {
        path: 'handicrafts',
        element: <Handicrafts />,
      },
      {
        path: 'anthropology',
        element: <Anthropology />,
      },
      {
        path: 'gorgor',
        element: <Gorgor />,
      },
      {
        path: 'basij',
        element: <Basij />,
      },
      {
        path: 'clor',
        element: <Clor />,
      },
      {
        path: 'demoli',
        element: <Demoli />,
      },
      {
        path: 'barandagh',
        element: <Barandagh />,
      },
      {
        path: 'kanjagh',
        element: <Kanjagh />,
      },
      {
        path: 'onar',
        element: <Onar />,
      },
      {
        path: 'martyr',
        element: <Martyr />,
      },
      {
        path: 'book',
        element: <Book />,
        loader: bookLoader,
      },
      {
        path: 'city',
        element: <City />,
      },
      {
        path: 'software',
        element: <Software />,
      },
      {
        path: 'vehicle',
        element: <Vehicle />,
      },
      {
        path: 'correspondence',
        element: <Correspondence />,
      },
      {
        path: 'Quran',
        element: <Quran title={`ترتیل کل قرآن کریم`} />,
      },
      {
        path: 'idea',
        children: [
          {
            path: '',
            element: <Idea title={`ایده های شهروندان`} />,
          },
          {
            path: 'new',
            element: <IdeaNew title={`ثبت ایده جدید`} />,
            loader: ideaNewLoader,
          },
          {
            path: 'edit/:id',
            element: <IdeaEdit title={`ویرایش درخواست`} />,
            loader: ideaEditLoader,
            action: ideaEditAction,
          },
          {
            path: 'list',
            element: <IdeaList title={`لیست ایده ها`} />,
            loader: () => {
              return { user: JSON.parse(localStorage.getItem('Response')) }
            },
          },
        ],
      },
      {
        path: 'complaint',
        children: [
          {
            path: '',
            element: <Complaint title={`ثبت شکایات`} />,
          },
          {
            path: 'new',
            element: <ComplaintNew title={`ثبت شکایت جدید`} />,
            loader: complaintNewLoader,
          },
          {
            path: 'edit/:id',
            element: <ComplaintEdit title={`ویرایش شکایت`} />,
            loader: complaintEditLoader,
            action: complaintEditAction,
          },
          {
            path: 'list',
            element: <ComplaintList title={`لیست شکایات`} />,
            loader: () => {
              return { user: JSON.parse(localStorage.getItem('Response')) }
            },
          },
        ],
      },
      {
        path: 'competition',
        children: [
          {
            path: '',
            element: <Competition title={`شرکت در مسابقات/ جشنواره`} />,
          },
          {
            path: 'new',
            element: <CompetitionNew title={`شرکت در مسابقات/ جشنواره`} />,
            loader: competitionNewLoader,
          },
          {
            path: 'edit/:id',
            element: <CompetitionEdit title={`ویرایش درخواست`} />,
            loader: competitionEditLoader,
            action: competitionEditAction,
          },
          {
            path: 'list',
            element: <CompetitionList title={`لیست مسابقات`} />,
            loader: () => {
              return { user: JSON.parse(localStorage.getItem('Response')) }
            },
          },
        ],
      },
      {
        path: 'course',
        children: [
          {
            path: '',
            element: <Course title={`ثبت نام کلاس آموزشی، فرهنگی و ورزشی`} />,
          },
          {
            path: 'sport',
            element: <CourseSport title={`ثبت نام کلاس های ورزشی`} />,
            loader: courseSportLoader,
          },
          {
            path: 'education',
            element: <CourseEducation title={`ثبت نام کلاس های آموزشی`} />,
            loader: courseEducationLoader,
          },
          {
            path: 'culture',
            element: <CourseCulture title={`ثبت نام کلاس های فرهنگی`} />,
            loader: courseCultureLoader,
          },
          // {
          //   path: 'edit/:id',
          //   element: <CompetitionEdit title={`ویرایش درخواست`} />,
          //   loader: competitionEditLoader,
          //   action: competitionEditAction,
          // },
          // {
          //   path: 'list',
          //   element: <CompetitionList title={`لیست مسابقات`} />,
          //   loader: () => {
          //     return { user: JSON.parse(localStorage.getItem('Response')) }
          //   },
          // },
        ],
      },
      {
        path: 'passage',
        children: [
          {
            path: '',
            element: <Passage title={`نامگذاری معابر`} />,
          },
          {
            path: 'new',
            element: <PassageNew title={`ارسال درخواست نامگذاری`} />,
            loader: passageNewLoader,
            action: passageNewAction,
          },
          {
            path: 'edit/:id',
            element: <PassageEdit title={`ویرایش درخواست`} />,
            loader: passageEditLoader,
            action: passageEditAction,
          },
          {
            path: 'list',
            element: <PassageList title={`لیست درخواست نامگذاری معابر`} />,
            loader: () => {
              return { user: JSON.parse(localStorage.getItem('Response')) }
            },
          },
          {
            path: 'print',
            element: <PassagePrint />,
          },
        ],
      },
      {
        path: 'tree',
        element: <Tree />,
      },
      {
        path: 'ardabil',
        element: <Ardabil />,
      },
      {
        path: 'link',
        loader: linkLoader,
        element: <Link />,
      },
      {
        path: 'map',
        children: [
          {
            path: '',
            element: <Map />,
          },
          {
            path: '1',
            element: <Area name={1} />,
          },
          {
            path: '2',
            element: <Area name={2} />,
          },
          {
            path: '3',
            element: <Area name={3} />,
          },
          {
            path: '4',
            element: <Area name={4} />,
          },
          {
            path: '5',
            element: <Area name={5} />,
          },
        ],
      },
      {
        path: 'billboard',
        element: <Billboard />,
      },
      {
        path: '133',
        element: <Platform133 />,
      },
      {
        path: 'mosque',
        children: [
          {
            index: true,
            element: <Mosque />,
          },
          {
            path: ':id',
            element: <Mosque />,
          },
        ]

      },
      {
        path: 'auction',
        element: <Auction />,
      },
      {
        path: 'tourism',
        children: [
          {
            index: true,
            element: <Tourism />,
          },
          {
            path: 'tourism-comments',
            element: <TourismComments />,
          },
          {
            path: 'discount',
            element: <Discount />,
          },
          {
            path: 'ardabil',
            children: [
              {
                index: true,
                element: <ArdabilTourism />,
              },
              {
                path: ':stateId',
                children: [
                  {
                    index: true,
                    element: <ArdabilTourismDetail />,
                  },
                  {
                    path: ':cityId',
                    element: <ArdabilTourismCityDetail />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'muharram',
        children: [
          {
            index: true,
            element: <Muharram title={`نوای محرم`} />,
          },
          {
            path: 'prayer',
            children: [
              {
                index: true,
                element: <MuharramPrayer title={`ادعیه`} />,
              },
              {
                path: ':prayerId',
                element: <MuharramPrayerContent title={`ادعیه`} />,
              },
            ],
          },
          {
            path: 'video',
            element: <PrayerVideo title={`مداحی`} />,
          },
          {
            path: 'calendar',
            children: [
              {
                index: true,
                element: <MuharramCalendar title={`تقویم عزاداری`} />,
              },
              {
                path: ':id',
                children: [
                  {
                    index: true,
                    element: <MuharramCalendar title={`تقویم عزاداری`} />,
                  },
                  {
                    path: ':field',
                    element: <MuharramCalendar title={`تقویم عزاداری`} />,
                  },
                  {
                    path: 'content/:order_id',
                    element: <MuharramCalendar title={`تقویم عزاداری`} />,
                  },
                ],
              },
            ],
          },
          {
            path: 'dasteh',
            children: [
              {
                index: true,
                element: <MuharramDasteh title={`دستجات عزاداری`} />,
              },
              {
                path: ':year',
                children: [
                  {
                    index: true,
                    element: <MuharramDasteh title={`دستجات عزاداری`} />,
                  },
                  {
                    path: ':id',
                    element: <MuharramDasteh title={`دستجات عزاداری`} />,
                  },
                ],
              },
            ],
          },
          {
            path: 'children',
            children: [
              {
                index: true,
                element: <MuharramChilderen title={`شیرخوارگان حسینی`} />,
              },
              {
                path: ':year',
                children: [
                  {
                    index: true,
                    element: <MuharramChilderen title={`شیرخوارگان حسینی`} />,
                  },
                  {
                    path: ':id',
                    element: <MuharramChilderen title={`شیرخوارگان حسینی`} />,
                  },
                ],
              },
            ],
          },
          {
            path: 'days',
            element: <MuharramPrayerDays title={`اعمال ماه محرم`} />,
          },
          {
            path: 'hadith',
            element: <MuharramHadith title={`احادیث امام حسین (ع)`} />,
          },
          {
            path: 'how-to-pray',
            element: <MuharramHowToPray title={`آداب زیارت امام حسین (ع)`} />,
          },
          {
            path: 'ashora',
            element: <MuharramAshora title={`فرهنگ عاشورا`} />,
          },
          {
            path: 'regulation',
            element: <MuharramRegulation title={`آیین عزاداری`} />,
          },
          {
            path: 'teshtgozarai',
            children: [
              {
                index: true,
                element: <MuharramTeshtgozari title={`طشت گذاری`} />,
              },
              {
                path: ':year',
                children: [
                  {
                    index: true,
                    element: <MuharramTeshtgozari title={`طشت گذاری`} />,
                  },
                  {
                    path: ':id',
                    element: <MuharramTeshtgozari title={`طشت گذاری`} />,
                  },
                ],
              },
            ],
          },
          {
            path: 'ashora-hosseini',
            element: <MuharramAshoraHosseini title={`عاشورای حسینی`} />,
          },
          {
            path: 'tasoa-hosseini',
            element: <MuharramTasoaHosseini title={`تاسوعای حسینی`} />,
          },
          {
            path: 'arbaein-hosseini',
            element: <MuharramArbaeinHosseini title={`اربعین حسینی`} />,
          },
          {
            path: 'candle-broadcast',
            element: <MuharramCandleBroadcast title={`آئین سنتی شمع گردانی`} />,
          },
          {
            path: 'welcome',
            children: [
              {
                index: true,
                element: <MuharramWelcome title={`استقبال از محرم`} />,
              },
              {
                path: ':year',
                element: <MuharramWelcome title={`استقبال از محرم`} />,
              },
            ],
          },
          {
            path: 'voice',
            element: <MuharramVoice title={`بانک صوت`} />,
          },
        ],
      },
      {
        path: 'transportation',
        element: <Transportation />,
      },
      {
        path: 'prayer-times',
        element: <PrayerTimes />,
      },
      {
        path: 'weather',
        element: <Weather />,
      },
      {
        path: 'citizenship-education',
        loader: citizenshipEducationLoader,
        element: <CitizenshipEducation />,
      },
      {
        path: 'think',
        element: <Think />,
      },
      {
        path: 'city-building',
        element: <CityBuilding />,
      },
      {
        path: 'parking',
        element: <Parking />,
      },
      {
        path: 'shohada',
        element: <Shohada />,
      },
      {
        path: 'brand-resource',
        element: <BrandResource />,
      },
      {
        path: 'setting',
        element: <Setting />,
      },
      {
        path: 'download',
        element: <Download />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },
      {
        path: 'telephone-number',
        loader: telephoneNumberLoader,
        element: <TelephoneNumber />,
      },
      {
        path: 'about',
        element: <About />,
      },
      {
        path: 'guide',
        element: <Guide />,
      },
    ],
  },
  {
    path: '*',
    // element: <Navigate to="/" replace />
    element: <>صفحه ی مورد نظر وجود ندارد</>,
  },
])

ReactDOM.createRoot(document.getElementById('root')).render(<RouterProvider router={router} />)
